/**
 * Combobox component - a searchable single-select.
 *
 * daisyUI has no combobox, so this composes its `input` and `menu`/
 * `dropdown-content` classes with the filtering and keyboard handling a
 * listbox needs. Prefer `SelectField` for a short fixed enum; reach for this
 * when the options are a dynamic collection whose labels are worth searching
 * (programmes, modules, people). With `onCreate` it also accepts a value no
 * option names, offered as the last row while the typed text matches no label.
 *
 * @module components/ui/Combobox/Combobox
 */

import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";

import { useEditLock, useLockedRefusal } from "@/components/ui/EditLock";
import { ReadOnlyValue } from "@/components/ui/Form/ReadOnlyValue";
import { Icon } from "@/components/ui/Icon";
import { useDropdownPlacement } from "@/components/ui/useDropdownPlacement";

export interface ComboboxOption {
  /** Option value */
  value: string;
  /** Option display label, rendered in full rather than truncated */
  label: string;
  /**
   * Secondary text shown alongside the label and matched by the filter, for a
   * short code or other identifier that disambiguates similar labels.
   */
  hint?: string;
  /** Whether the option is disabled */
  disabled?: boolean;
}

export interface ComboboxProps {
  /** Unique ID for the input */
  id: string;
  /** Value of the selected option, or "" when nothing is selected */
  value: string;
  /** Change handler, called with the selected option's value */
  onChange: (value: string) => void;
  /** Selectable options */
  options: ComboboxOption[];
  /** Placeholder shown when nothing is selected */
  placeholder?: string;
  /** Message shown when the search matches nothing */
  emptyMessage?: string;
  /**
   * Refuses activation but stays focusable, as `aria-disabled` plus a guard
   * on every interaction: a native `disabled` leaves the tab order and takes
   * its reason along.
   */
  disabled?: boolean;
  /** Why it is disabled: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  /** Whether the field is required */
  required?: boolean;
  /**
   * Phosphor icon name shown inside the field, as {@link Icon} takes it.
   * Defaults to the magnifying glass, which is what tells a reader this field
   * is searched rather than picked from. Pass `null` for no icon.
   */
  icon?: string | null;
  /** Size variant */
  size?: "sm" | "lg";
  /** Additional CSS classes */
  className?: string;
  /** Test ID, applied to the text input */
  "data-testid"?: string;
  /** Aria describedby ID */
  "aria-describedby"?: string;
  /** Accessible name, for a combobox with no visible `<label for>` of its own */
  "aria-label"?: string;
  /**
   * Makes the list creatable: typed text no option label matches is offered
   * as a last row, and choosing it calls this with the trimmed text. The
   * caller stores it, so `value` may then name no option.
   */
  onCreate?: (text: string) => void;
  /** How the create row reads. Defaults to `Add "<text>"`. */
  createLabel?: (text: string) => string;
}

/**
 * Size classes written out in full.
 *
 * Tailwind only scans `src/`, and it scans it as text: a class assembled at
 * runtime from a prop is never generated, and the control silently renders at
 * its default size. Spelling them out here is what keeps them off the
 * `@source inline` safelist in `src/style.css`.
 */
const SIZE_CLASS = { sm: "input-sm", lg: "input-lg" } as const;

/** The text a given option contributes to the filter. */
function searchText(option: ComboboxOption): string {
  return option.hint ? `${option.label} ${option.hint}` : option.label;
}

/** Stands in for the create row in the list; no caller's option can be it. */
interface CreateRow {
  create: string;
}

type Row = ComboboxOption | CreateRow;

function isCreateRow(row: Row | undefined): row is CreateRow {
  return row !== undefined && "create" in row;
}

const defaultCreateLabel = (text: string): string => `Add "${text}"`;

/** How a selected option reads back in the closed input. */
function displayText(option: ComboboxOption): string {
  return option.hint ? `${option.label} (${option.hint})` : option.label;
}

export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder = undefined,
  emptyMessage = "No matches",
  disabled: ownDisabled = false,
  required = false,
  icon = "magnifying-glass",
  size = undefined,
  className = "",
  "data-testid": testId,
  "aria-describedby": ariaDescribedby,
  "aria-label": ariaLabel,
  onCreate = undefined,
  createLabel = defaultCreateLabel,
  ...reasonProps
}: ComboboxProps) {
  // Left in the rest so its presence is readable: a caller that can refuse
  // with a reason keeps one wrapper throughout, so the control is not
  // remounted, and does not lose focus, as the reason comes and goes.
  const { disabled, offersReason, disabledReason } = useLockedRefusal(ownDisabled, reasonProps);
  const { locked } = useEditLock();
  const [listRequested, setOpen] = useState(false);
  // A refusal closes a list already open, so nothing in it can be chosen.
  const open = listRequested && !disabled;
  const [query, setQuery] = useState("");
  // And ends the request, or the list reopens by itself when the refusal lifts.
  if (disabled && listRequested) {
    setOpen(false);
    setQuery("");
  }
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const fieldRef = useRef<HTMLLabelElement>(null);
  // Fixed to the viewport, so a table's `overflow-x-auto` or a modal's scroll
  // box cannot cut the list to the row it opens from.
  useDropdownPlacement({
    anchorRef: fieldRef,
    panelRef: listRef,
    open,
    matchWidth: true,
    align: "start",
  });
  const listboxId = `${useId()}-listbox`;
  const reasonId = useId();
  const reason = disabled && disabledReason ? disabledReason : undefined;
  const describedBy =
    [ariaDescribedby, reason ? reasonId : undefined].filter(Boolean).join(" ") || undefined;

  const selected = options.find((option) => option.value === value);
  const matches = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    if (!term) {
      return options;
    }
    return options.filter((option) => searchText(option).toLocaleLowerCase().includes(term));
  }, [options, query]);
  const createText = query.trim();
  const offersCreate =
    onCreate !== undefined &&
    createText !== "" &&
    !options.some((option) => option.label.toLocaleLowerCase() === createText.toLocaleLowerCase());
  const rows: Row[] = offersCreate ? [...matches, { create: createText }] : matches;

  // Closing resets the query, so the input falls back to rendering the
  // selection. Without that a half-typed search would sit in the box looking
  // like the selected value.
  const close = (): void => {
    setOpen(false);
    setQuery("");
  };

  const openList = (): void => {
    if (disabled) {
      return;
    }
    setOpen(true);
    setQuery("");
    setActiveIndex(0);
  };

  const select = (row: Row | undefined): void => {
    if (disabled || !row) {
      return;
    }
    if (isCreateRow(row)) {
      onCreate?.(row.create);
      close();
      return;
    }
    if (row.disabled) {
      return;
    }
    onChange(row.value);
    close();
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Keyboard navigation has to bring its own scrolling; the list only shows a
  // few rows at a time and the active option would otherwise move out of sight.
  // Optional-call because jsdom has no layout and omits scrollIntoView entirely.
  useEffect(() => {
    if (!open) {
      return;
    }
    listRef.current?.children[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      setActiveIndex((index) => (rows.length ? (index + 1) % rows.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      setActiveIndex((index) => (rows.length ? (index - 1 + rows.length) % rows.length : 0));
    } else if (event.key === "Enter") {
      if (open) {
        event.preventDefault();
        select(rows[activeIndex]);
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        close();
      }
    } else if (event.key === "Tab") {
      close();
    }
  };

  const activeOptionId = open && rows.length ? `${listboxId}-option-${activeIndex}` : undefined;
  // Open, the box is the search field and shows the query; closed, it reads
  // back the selection.
  let inputText = "";
  if (open) {
    inputText = query;
  } else if (selected) {
    inputText = displayText(selected);
  } else if (onCreate) {
    // A created value names no option, and is still the selection.
    inputText = value;
  }

  if (locked) {
    return (
      <ReadOnlyValue
        id={id}
        // A stored value no option names reads as stored, never as unset.
        value={selected ? displayText(selected) : value}
        data-testid={testId}
        aria-describedby={ariaDescribedby}
        aria-label={ariaLabel}
      />
    );
  }

  const combobox = (
    <div className={`relative w-full${className ? ` ${className}` : ""}`} ref={containerRef}>
      {/* daisyUI puts an icon inside a field by making the field a wrapper and
          the control a child of it. The wrapper carries no text of its own, so
          the accessible name still comes from the caller's `<label for>`. */}
      <label
        ref={fieldRef}
        className={`input input-bordered w-full${size ? ` ${SIZE_CLASS[size]}` : ""}${
          disabled ? " cursor-not-allowed bg-base-200" : ""
        }`}
        htmlFor={id}
      >
        {icon ? <Icon name={icon} className="opacity-50" /> : null}
        <input
          id={id}
          type="text"
          role="combobox"
          autoComplete="off"
          className={`grow${disabled ? " cursor-not-allowed" : ""}`}
          value={inputText}
          placeholder={placeholder}
          required={required}
          onChange={(event) => {
            if (disabled) {
              return;
            }
            setQuery(event.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onClick={openList}
          onKeyDown={(event) => {
            if (!disabled) {
              handleKeyDown(event);
            }
          }}
          data-testid={testId}
          aria-describedby={describedBy}
          aria-label={ariaLabel}
          aria-required={required}
          aria-disabled={disabled || undefined}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
        />
      </label>
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          className="menu dropdown-content bg-base-100 rounded-box border-base-300 absolute z-10 mt-1 max-h-72 w-full flex-nowrap overflow-y-auto border p-2 shadow-sm"
        >
          {matches.map((option, index) => (
            // `role="none"`: a listbox admits only options as children, and
            // the `<li>` is daisyUI's menu structure rather than anything a
            // reader should meet between the list and its option.
            <li
              key={option.value}
              role="none"
              className={option.disabled ? "menu-disabled" : undefined}
            >
              <button
                type="button"
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={option.value === value}
                disabled={option.disabled}
                // A listbox option has no `value` attribute the way `<option>`
                // does, and the label is not the identity. This is what lets a
                // caller assert on which options are offered.
                data-value={option.value}
                className={`flex w-full items-baseline justify-between gap-2 text-left${
                  index === activeIndex ? " menu-active" : ""
                }`}
                // A click lands after the input's blur, so selection is wired
                // to mousedown instead; preventDefault keeps focus in the input.
                onMouseDown={(event) => {
                  event.preventDefault();
                  select(option);
                }}
              >
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.hint && <span className="text-muted shrink-0 text-xs">{option.hint}</span>}
              </button>
            </li>
          ))}
          {offersCreate && (
            <li role="none">
              <button
                type="button"
                id={`${listboxId}-option-${matches.length}`}
                role="option"
                aria-selected={false}
                className={`flex w-full items-baseline gap-2 text-left${
                  activeIndex === matches.length ? " menu-active" : ""
                }`}
                data-testid={testId ? `${testId}-create` : undefined}
                onMouseDown={(event) => {
                  event.preventDefault();
                  select({ create: createText });
                }}
              >
                <Icon name="plus" className="shrink-0" />
                <span className="min-w-0 flex-1 truncate">{createLabel(createText)}</span>
              </button>
            </li>
          )}
          {!rows.length && (
            <li role="none" className="menu-disabled">
              <span className="text-muted text-sm">{emptyMessage}</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
  if (!offersReason) {
    return combobox;
  }
  return (
    <span className={reason ? "tooltip" : "contents"} data-tip={reason}>
      {combobox}
      {reason ? (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </span>
  );
}

export default Combobox;
